import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Target, ArrowRight, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import { useGoals, useAccounts, useWallets, useTransactions } from "@/hooks/queries";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useAccountBalances } from "@/hooks/useAccountBalances";
import { Link } from "react-router-dom";
import { parseLocalDate } from "@/lib/dates";

const CELEBRATED_KEY = "celebrated_goals";

const getCelebratedIds = (): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(CELEBRATED_KEY) || "[]"));
  } catch {
    return new Set();
  }
};

const markAsCelebrated = (id: string) => {
  const ids = getCelebratedIds();
  ids.add(id);
  localStorage.setItem(CELEBRATED_KEY, JSON.stringify([...ids]));
};

export const GoalSpotlight = () => {
  const { data: goals = [], isLoading } = useGoals();
  const { data: accounts = [] } = useAccounts();
  const { data: wallets = [] } = useWallets();
  const { data: transactions = [] } = useTransactions();
  const { formatCurrency } = useUserPreferences();

  const [celebratedIds, setCelebratedIds] = useState<Set<string>>(getCelebratedIds);
  const [celebrating, setCelebrating] = useState<string | null>(null);

  const { getAccountBalance } = useAccountBalances(
    transactions,
    accounts.map(a => ({
      id: a.id,
      name: a.name,
      initial_balance: a.initial_balance,
      balance_as_of_date: a.balance_as_of_date,
    })),
    wallets.map(w => ({ id: w.id, account_id: w.account_id, name: w.name, balance: w.balance }))
  );

  const getEffectiveAmount = useCallback(
    (goal: (typeof goals)[number]): number => {
      if (goal.linked_account_id) {
        const linked = accounts.find(a => a.id === goal.linked_account_id);
        if (linked) return Math.max(0, getAccountBalance(linked));
      }
      return goal.current_amount || 0;
    },
    [accounts, getAccountBalance]
  );

  const getProgress = useCallback(
    (goal: (typeof goals)[number]) =>
      Math.min(Math.round((getEffectiveAmount(goal) / goal.target_amount) * 100), 100),
    [getEffectiveAmount]
  );

  // Exclude already-celebrated goals; pick the one closest to completion
  const eligibleGoals = goals.filter(g => !celebratedIds.has(g.id));
  const spotlight = [...eligibleGoals].sort((a, b) => {
    const pa = getProgress(a);
    const pb = getProgress(b);
    if (pb !== pa) return pb - pa;
    if (a.target_date && b.target_date) return a.target_date.localeCompare(b.target_date);
    return 0;
  })[0] ?? null;

  const spotlightProgress = spotlight ? getProgress(spotlight) : 0;
  const isComplete = spotlight !== null && spotlightProgress >= 100;

  // Trigger celebration then auto-dismiss after 8 seconds
  useEffect(() => {
    if (!spotlight || !isComplete) return;
    if (celebratedIds.has(spotlight.id)) return;

    setCelebrating(spotlight.id);

    const timer = setTimeout(() => {
      markAsCelebrated(spotlight.id);
      setCelebratedIds(prev => new Set([...prev, spotlight.id]));
      setCelebrating(null);
    }, 8000);

    return () => clearTimeout(timer);
  }, [spotlight?.id, isComplete]);

  if (isLoading) return null;

  // No goals at all
  if (goals.length === 0) {
    return (
      <Card className="relative overflow-hidden group transition-all duration-300 hover:shadow-lg border-border/40">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-income to-income/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <CardContent className="flex items-center justify-between py-5 px-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-income/10">
              <Target className="h-4 w-4 text-income-text" />
            </div>
            <div>
              <p className="text-sm font-medium">No goals yet</p>
              <p className="text-xs text-muted-foreground">Set a goal to keep your motivation front and centre</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" asChild className="gap-1 text-xs shrink-0">
            <Link to="/app/planning?tab=goals">
              Add a goal <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  // All eligible goals have been celebrated and dismissed
  if (!spotlight) {
    return (
      <Card className="relative overflow-hidden border-yellow-500/40 bg-yellow-500/5">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 to-amber-500" />
        <CardContent className="flex items-center justify-between py-5 px-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-500/15">
              <Trophy className="h-4 w-4 text-yellow-500" />
            </div>
            <div>
              <p className="text-sm font-medium">All goals achieved! 🏆</p>
              <p className="text-xs text-muted-foreground">Ready for your next challenge?</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" asChild className="gap-1 text-xs shrink-0">
            <Link to="/app/planning?tab=goals">
              New goal <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Celebration card (shown for 8 seconds when a goal just hits 100%)
  if (isComplete) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="relative overflow-hidden border-yellow-500/50 bg-yellow-500/5">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 to-amber-500" />
          <CardHeader className="pb-2 pt-5">
            <CardTitle className="text-base font-bold flex items-center gap-2">
              <div className="p-2 rounded-lg bg-yellow-500/15">
                <Trophy className="h-4 w-4 text-yellow-500" />
              </div>
              Goal Achieved! 🎉
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-5 space-y-3">
            <div>
              <p className="font-semibold text-sm">{spotlight.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Congratulations! You've reached your target of{" "}
                <span className="font-medium">{formatCurrency(spotlight.target_amount)}</span>.
                Outstanding work! 💪
              </p>
            </div>
            <Progress value={100} className="h-2 [&>div]:bg-yellow-500" />
            <p className="text-xs text-muted-foreground italic">
              This goal will clear from your dashboard shortly…
            </p>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // Normal in-progress spotlight
  const remaining = spotlight.target_amount - getEffectiveAmount(spotlight);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <Card className="relative overflow-hidden group transition-all duration-300 hover:shadow-lg border-border/40">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-income to-income/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <CardHeader className="pb-2 pt-5">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-bold flex items-center gap-2">
              <div className="p-2 rounded-lg bg-income/10">
                <Target className="h-4 w-4 text-income-text" />
              </div>
              Goal in Focus
            </CardTitle>
            {goals.length > 1 && (
              <Button variant="ghost" size="sm" asChild className="gap-1 text-xs text-muted-foreground">
                <Link to="/app/planning?tab=goals">
                  All goals <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="pb-5 space-y-3">
          <div>
            <p className="font-semibold text-sm">{spotlight.name}</p>
            {spotlight.motivation && (
              <p className="text-xs italic text-muted-foreground mt-0.5">
                ✦ {spotlight.motivation}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{spotlightProgress}% there</span>
              <span>{formatCurrency(remaining)} to go</span>
            </div>
            <Progress value={spotlightProgress} className="h-2" />
          </div>

          {spotlight.target_date && (
            <p className="text-xs text-muted-foreground">
              Target:{" "}
              {parseLocalDate(spotlight.target_date).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};
